<template>
  <div v-bind="$attrs" class="video-container">
    <div v-if="isLoading" class="video-loading-spinner">
      <div class="spinner"></div>
    </div>
    <video
      ref="videoElement"
      :src="props.videoUrlPreview"
      :controls="!props.autoplay"
      :muted="props.autoplay"
      :loop="props.loop"
      playsinline
      :autoplay="props.autoplay"
      preload="none"
      @loadeddata="handleVideoLoaded"
      @canplay="handleVideoLoaded"
    />
    <WidgetPlayBtn
      class="play-button"
      @click="toggleFullScreen"
      :button-txt="buttonTxt"
      v-if="buttonTxt"
    />
  </div>
  <div v-if="isFullScreen" ref="overlayElement" class="overlay">
    <div v-if="isOverlayLoading" class="video-loading-spinner">
      <div class="spinner"></div>
    </div>
    <video
      ref="overlayVideoElement"
      :src="props.videoUrl"
      :controls="props.autoplay"
      :muted="!props.autoplay"
      :loop="props.loop"
      playsinline
      :autoplay="props.autoplay"
      preload="none"
      @loadeddata="handleOverlayVideoLoaded"
      @canplay="handleOverlayVideoLoaded"
    />
    <div ref="closeButtonElement" class="close-button" @click="closeOverlay">
      <CloseIcon />
    </div>
  </div>
</template>

<script setup>
import gsap from 'gsap'
import CloseIcon from '@/assets/img/ico/close.svg'

const props = defineProps({
  videoUrl: {
    type: String,
    default: '',
  },
  videoUrlPreview: {
    type: String,
    default: '',
  },
  buttonTxt: {
    type: String,
    default: 'Play',
  },
  autoplay: Boolean,
  loop: Boolean,
})

const emit = defineEmits(['play', 'pause'])

const videoElement = ref(null)
const overlayElement = ref(null)
const overlayVideoElement = ref(null)
const closeButtonElement = ref(null)
const isPlaying = ref(false)
const isFullScreen = ref(false)
const isLoading = ref(true)
const isOverlayLoading = ref(true)
let observer = null

const handleVideoLoaded = () => {
  isLoading.value = false
}

const handleOverlayVideoLoaded = () => {
  isOverlayLoading.value = false
}

const toggleFullScreen = (event) => {
  if (event) {
    event.stopPropagation()
  }
  if (isFullScreen.value) {
    gsap
      .timeline()
      .to(overlayVideoElement.value, {
        opacity: 0,
        duration: 0.5,
        ease: 'power2.out',
      })
      .to(overlayElement.value, {
        x: '-100%',
        duration: 0.5,
        ease: 'power2.out',
      })
      .add(() => {
        isFullScreen.value = false
        lenis.start()
      })
  } else {
    isFullScreen.value = true
    lenis.stop()
    if (closeButtonElement.value) {
      closeButtonElement.value.classList.remove('hidden')
    }
  }
}

const closeOverlay = (event) => {
  event.stopPropagation()
  if (closeButtonElement.value) {
    closeButtonElement.value.classList.add('hidden')
  }
  toggleFullScreen(event)
}

watch(
  () => props.playInViewport,
  (playInViewport) => {
    if (playInViewport) {
      playerObserver()
    }
  },
)

watch(
  () => props.forcePlay,
  (forcePlay) => {
    forcePlay ? videoElement.value?.play() : videoElement.value?.pause()
  },
)

watch(isFullScreen, async (newVal) => {
  await nextTick()
  if (newVal) {
    gsap
      .timeline()
      .fromTo(
        overlayElement.value,
        { x: '-100%' },
        { x: '0%', duration: 0.5, ease: 'power2.out' },
      )
      .fromTo(
        overlayVideoElement.value,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.5, ease: 'power2.out' },
        '+=0.3',
      )
  }
})

const playerObserver = () => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          emit('play')
          entry.target.play()
          isPlaying.value = true
        } else {
          emit('pause')
          entry.target.pause()
          isPlaying.value = false
        }
      })
    },
    {
      threshold: 0.1,
    },
  )

  observer.observe(videoElement.value)

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      videoElement.value?.pause()
      isPlaying.value = false
    } else {
      videoElement.value?.play()
      isPlaying.value = true
    }
  })
}

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

defineExpose({
  videoElement,
  overlayElement,
  overlayVideoElement,
  closeButtonElement,
})
</script>

<script>
export default {
  name: 'WidgetVideo',
}
</script>

<style lang="scss">
@import './style.scss';
</style>
